// src/pages/CRM/ActivityComm/TaskForm.js

import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, MenuItem, FormControl, InputLabel, Select,
  CircularProgress, Alert, Box, Typography, Switch, FormControlLabel,
  IconButton, Divider, List, ListItem, ListItemIcon, ListItemText,
  ListItemSecondaryAction, Checkbox, Autocomplete,
  useMediaQuery, useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  NotificationsActive as ReminderIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';
import { taskService } from './activityCommService';
import {
  TaskPriorityOptions, TaskStatusOptions,
  RecurrenceFrequencyOptions, ReminderTypeOptions
} from './activityCommEnums';

const emptyFormData = {
  title: '',
  description: '',
  priority: 'MEDIUM',
  status: 'PENDING',
  due_date: '',
  start_date: '',
  assigned_to: null,
  contact_id: null,
  account_id: null,
  lead_id: null,
  is_recurring: false,
  recurrence_frequency: 'DAILY',
  recurrence_interval: 1,
  recurrence_end_type: 'never',
  recurrence_end_count: '',
  recurrence_end_date: '',
  checklist: [],
  reminders: []
};

const TaskForm = ({ open, onClose, task, onSuccess }) => {
  const { chatbotId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isEdit = Boolean(task);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ ...emptyFormData });

  // Dropdown data for Autocomplete fields
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);

  const loadDropdownData = async () => {
    try {
      const params = chatbotId ? { chatbot_id: chatbotId, per_page: 100 } : { per_page: 100 };

      const [contactsRes, accountsRes, leadsRes, usersRes] = await Promise.allSettled([
        axiosInstance.get('/api/crm/contacts', { params }),
        axiosInstance.get('/api/crm/accounts', { params }),
        axiosInstance.get('/api/crm/leads', { params }),
        axiosInstance.get('/api/users/team-members', { params: { limit: 100 } }),
      ]);

      if (contactsRes.status === 'fulfilled' && contactsRes.value.data.success) {
        setContacts(contactsRes.value.data.data || []);
      }
      if (accountsRes.status === 'fulfilled' && accountsRes.value.data.success) {
        setAccounts(accountsRes.value.data.data || []);
      }
      if (leadsRes.status === 'fulfilled' && leadsRes.value.data.success) {
        setLeads(leadsRes.value.data.data || []);
      }
      if (usersRes.status === 'fulfilled' && usersRes.value.data.success) {
        setUsers(usersRes.value.data.data || []);
      }
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  };

  useEffect(() => {
    if (open) {
      loadDropdownData();
      if (task) {
        setFormData({
          title: task.title || '',
          description: task.description || '',
          priority: task.priority || 'MEDIUM',
          status: task.status || 'PENDING',
          due_date: task.due_date ? formatDateTimeLocal(task.due_date) : '',
          start_date: task.start_date ? formatDateTimeLocal(task.start_date) : '',
          assigned_to: task.assigned_to || null,
          contact_id: task.contact_id || null,
          account_id: task.account_id || null,
          lead_id: task.lead_id || null,
          is_recurring: task.is_recurring || false,
          recurrence_frequency: task.recurrence_frequency || 'DAILY',
          recurrence_interval: task.recurrence_interval || 1,
          recurrence_end_type: task.recurrence_end_date ? 'on_date' : task.recurrence_end_count ? 'after_count' : 'never',
          recurrence_end_count: task.recurrence_end_count || '',
          recurrence_end_date: task.recurrence_end_date ? formatDateTimeLocal(task.recurrence_end_date) : '',
          checklist: task.checklist || [],
          reminders: task.reminders || []
        });
      } else {
        setFormData({ ...emptyFormData });
      }
      setError(null);
    }
  }, [open, task]);

  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Checklist handlers
  const handleAddChecklistItem = () => {
    setFormData(prev => ({
      ...prev,
      checklist: [...prev.checklist, { text: '', is_completed: false }]
    }));
  };

  const handleChecklistTextChange = (index, text) => {
    setFormData(prev => {
      const updated = [...prev.checklist];
      updated[index] = { ...updated[index], text };
      return { ...prev, checklist: updated };
    });
  };

  const handleChecklistToggle = (index) => {
    setFormData(prev => {
      const updated = [...prev.checklist];
      updated[index] = { ...updated[index], is_completed: !updated[index].is_completed };
      return { ...prev, checklist: updated };
    });
  };

  const handleRemoveChecklistItem = (index) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };

  // Reminder handlers
  const handleAddReminder = () => {
    setFormData(prev => ({
      ...prev,
      reminders: [...prev.reminders, { reminder_type: 'IN_APP', remind_at: '', message: '' }]
    }));
  };

  const handleReminderChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.reminders];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, reminders: updated };
    });
  };

  const handleRemoveReminder = (index) => {
    setFormData(prev => ({
      ...prev,
      reminders: prev.reminders.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        status: formData.status,
        due_date: formData.due_date || null,
        start_date: formData.start_date || null,
        assigned_to: formData.assigned_to || null,
        contact_id: formData.contact_id || null,
        account_id: formData.account_id || null,
        lead_id: formData.lead_id || null,
        is_recurring: formData.is_recurring,
        checklist: formData.checklist.filter(item => item.text.trim()),
        reminders: formData.reminders.filter(r => r.remind_at)
      };

      if (chatbotId) data.chatbot_id = chatbotId;

      if (formData.is_recurring) {
        data.recurrence_frequency = formData.recurrence_frequency;
        data.recurrence_interval = formData.recurrence_interval;
        if (formData.recurrence_end_type === 'after_count') {
          data.recurrence_end_count = formData.recurrence_end_count;
        } else if (formData.recurrence_end_type === 'on_date') {
          data.recurrence_end_date = formData.recurrence_end_date || null;
        }
      }

      let response;
      if (isEdit) {
        response = await taskService.update(task.id, data);
      } else {
        response = await taskService.create(data);
      }

      if (response.success) {
        enqueueSnackbar(
          isEdit ? 'Task updated successfully' : 'Task created successfully',
          { variant: 'success' }
        );
        if (onSuccess) onSuccess();
      } else {
        setError(response.message || 'Failed to save task');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save task');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle>
        {isEdit ? 'Edit Task' : 'Create Task'}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              required
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Task title"
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Task description..."
            />
          </Grid>

          {/* Priority */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                {TaskPriorityOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Status */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleChange('status', e.target.value)}
              >
                {TaskStatusOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Due Date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Due Date"
              type="datetime-local"
              value={formData.due_date}
              onChange={(e) => handleChange('due_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="datetime-local"
              value={formData.start_date}
              onChange={(e) => handleChange('start_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Assigned To */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => {
                if (!option) return '';
                return option.name || option.email || 'Unknown';
              }}
              value={users.find(u => u.id === formData.assigned_to) || null}
              onChange={(_, value) => handleChange('assigned_to', value?.id || null)}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option.id === value.id;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Assign To" placeholder="Select assignee" />
              )}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box component="li" key={key} {...restProps}>
                    <Box>
                      <Typography variant="body2">{option.name || option.email}</Typography>
                      {option.name && (
                        <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
            />
          </Grid>

          {/* Contact */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={contacts}
              getOptionLabel={(option) => {
                if (!option) return '';
                return option.full_name || `${option.first_name || ''} ${option.last_name || ''}`.trim();
              }}
              value={contacts.find(c => c.id === formData.contact_id) || null}
              onChange={(_, value) => handleChange('contact_id', value?.id || null)}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} label="Contact" placeholder="Select contact" />
              )}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box component="li" key={key} {...restProps}>
                    <Box>
                      <Typography variant="body2">
                        {option.full_name || `${option.first_name || ''} ${option.last_name || ''}`.trim()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                    </Box>
                  </Box>
                );
              }}
            />
          </Grid>

          {/* Account */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={accounts}
              getOptionLabel={(option) => option?.name || ''}
              value={accounts.find(a => a.id === formData.account_id) || null}
              onChange={(_, value) => handleChange('account_id', value?.id || null)}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} label="Account" placeholder="Select account" />
              )}
            />
          </Grid>

          {/* Lead */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={leads}
              getOptionLabel={(option) => {
                if (!option) return '';
                return `${option.first_name || ''} ${option.last_name || ''}`.trim() || option.email || '';
              }}
              value={leads.find(l => l.id === formData.lead_id) || null}
              onChange={(_, value) => handleChange('lead_id', value?.id || null)}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} label="Lead" placeholder="Select lead" />
              )}
            />
          </Grid>

          {/* Is Recurring */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_recurring}
                  onChange={(e) => handleChange('is_recurring', e.target.checked)}
                />
              }
              label="Recurring Task"
            />
          </Grid>

          {/* Recurrence Settings */}
          {formData.is_recurring && (
            <>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={formData.recurrence_frequency}
                    label="Frequency"
                    onChange={(e) => handleChange('recurrence_frequency', e.target.value)}
                  >
                    {RecurrenceFrequencyOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Every X"
                  type="number"
                  value={formData.recurrence_interval}
                  onChange={(e) => handleChange('recurrence_interval', parseInt(e.target.value, 10) || 1)}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>End Type</InputLabel>
                  <Select
                    value={formData.recurrence_end_type}
                    label="End Type"
                    onChange={(e) => handleChange('recurrence_end_type', e.target.value)}
                  >
                    <MenuItem value="never">Never</MenuItem>
                    <MenuItem value="after_count">After Count</MenuItem>
                    <MenuItem value="on_date">On Date</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {formData.recurrence_end_type === 'after_count' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="End After Count"
                    type="number"
                    value={formData.recurrence_end_count}
                    onChange={(e) => handleChange('recurrence_end_count', parseInt(e.target.value, 10) || '')}
                    inputProps={{ min: 1 }}
                    placeholder="Number of occurrences"
                  />
                </Grid>
              )}

              {formData.recurrence_end_type === 'on_date' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="datetime-local"
                    value={formData.recurrence_end_date}
                    onChange={(e) => handleChange('recurrence_end_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
            </>
          )}

          {/* Checklist Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>Checklist</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddChecklistItem}
              >
                Add Item
              </Button>
            </Box>
            {formData.checklist.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No checklist items. Click "Add Item" to add one.
              </Typography>
            ) : (
              <List dense disablePadding>
                {formData.checklist.map((item, index) => (
                  <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={item.is_completed}
                        onChange={() => handleChecklistToggle(index)}
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <TextField
                          fullWidth
                          size="small"
                          variant="standard"
                          placeholder="Checklist item..."
                          value={item.text}
                          onChange={(e) => handleChecklistTextChange(index, e.target.value)}
                          sx={{
                            '& .MuiInput-input': {
                              textDecoration: item.is_completed ? 'line-through' : 'none',
                              color: item.is_completed ? 'text.secondary' : 'text.primary'
                            }
                          }}
                        />
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small" onClick={() => handleRemoveChecklistItem(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>

          {/* Reminders Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                <ReminderIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Reminders
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddReminder}
              >
                Add Reminder
              </Button>
            </Box>
            {formData.reminders.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No reminders. Click "Add Reminder" to add one.
              </Typography>
            ) : (
              formData.reminders.map((reminder, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={reminder.reminder_type}
                      label="Type"
                      onChange={(e) => handleReminderChange(index, 'reminder_type', e.target.value)}
                    >
                      {ReminderTypeOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Date & Time"
                    type="datetime-local"
                    value={reminder.remind_at}
                    onChange={(e) => handleReminderChange(index, 'remind_at', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    size="small"
                    label="Message"
                    value={reminder.message}
                    onChange={(e) => handleReminderChange(index, 'message', e.target.value)}
                    placeholder="Reminder message"
                    sx={{ flex: 1 }}
                  />
                  <IconButton size="small" onClick={() => handleRemoveReminder(index)} sx={{ mt: 0.5 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {isEdit ? 'Update Task' : 'Create Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskForm;
